// file: apps/web-pharmacy/src/components/alertes/alerte-equipe.config.js
/**
 * PHARMAKON — Alertes équipe : la configuration CLIENT.
 *
 * PLAN_mobile_pharma_owner_manager §2.6 / §3.4 / §3.5.
 *
 * PRINCIPE : le serveur fait autorité sur les CADENCES. Chaque réponse de
 * `/pending-count` et de `/actives` porte `poll_ms`, `poll_cache_ms`,
 * `poll_urgent_ms`, `poll_stream_ms`, `backoff_max_ms`, `max_defer_ms` et
 * `max_bannieres` ; le client les HONORE. Les valeurs ci-dessous ne sont que le
 * repli utilisé avant la toute première réponse (démarrage à froid, ou API
 * injoignable) — jamais une seconde source de vérité (L-2).
 */

/** Repli de démarrage — remplacé par la première réponse du serveur. */
export const ALERTE_CLIENT_DEFAUTS = Object.freeze({
  pollMs: 15_000,
  pollCacheMs: 60_000,
  pollUrgentMs: 5_000,
  pollStreamMs: 60_000,
  backoffMaxMs: 120_000,
  echecsAvantBackoff: 3,
  maxDeferMs: 20_000,
  maxBannieres: 2,
  sseEnabled: true,
});

/** L'origine d'accusé annoncée au serveur depuis ce client. */
export const ACK_SOURCE = "web";

/**
 * SOUPAPE DE SÉCURITÉ COMPTOIR (§3.5) — les chemins de MUTATION d'encaissement.
 *
 * Une surcouche bloquante qui vole le focus pendant une de ces requêtes est un
 * INCIDENT DE CAISSE, pas une gêne. Tant qu'une de ces requêtes est en vol, la
 * surcouche est mise en file ; elle se monte au premier temps mort, et de toute
 * façon au bout de `max_defer_ms` (le report est alors journalisé en console).
 *
 * Le repérage se fait sur l'URL au niveau du client HTTP partagé : c'est le
 * SEUL point de passage commun aux ~10 sites de mutation (vente libre, CHIFA,
 * validation, crédit, avoir, retour, trésorerie, caisse). Les instrumenter un
 * par un aurait demandé de toucher des écrans que ce lot ne possède pas — et
 * l'un d'eux aurait fini par être oublié.
 */
export const CHEMINS_CRITIQUES_POS = Object.freeze([
  /\/ventes\/libre\b/,
  /\/ventes\/chifa\b/,
  /\/ventes\/[^/]+\/validate\b/,
  /\/ventes\/[^/]+\/cancel\b/,
  /\/instance-chifa\/[^/]+\/(valider|cloturer)\b/,
  /\/tresorerie\/(encaissements|decaissements|transferts)\b/,
  /\/tresorerie\/mouvements\/[^/]+\/annuler\b/,
  /\/caisse\/(open|[^/]+\/(close|reopen))\b/,
  /\/credits\/[^/]+\/(reglement|reglements)\b/,
  /\/avoirs\/[^/]+\/(valider|payer)\b/,
  /\/retours\/[^/]+\/(valider|rembourser)\b/,
]);

/** Les verbes qui MUTENT — un GET ne bloque jamais rien. */
export const METHODES_MUTANTES = Object.freeze(["post", "put", "patch", "delete"]);

/** Libellés des trois paliers (français, vouvoiement — §1.5). */
export const LIBELLE_PALIER = Object.freeze({
  INFO: "Information",
  WARNING: "Important",
  CRITICAL: "URGENT",
});

/** L'événement fenêtre qui ouvre le panneau de rédaction (voir README). */
export const EVENEMENT_OUVRIR_PANNEAU = "pharmakon:alertes-equipe:ouvrir";

/** Le raccourci clavier d'ouverture (clavier d'abord — aucun ornement permanent). */
export const RACCOURCI_PANNEAU = Object.freeze({
  ctrl: true,
  shift: true,
  key: "a",
  libelle: "Ctrl + Maj + A",
});

// ─────────────────────────────────────────────────────────────────────────────
// GARDES D'INTERFACE — miroir de `PERMISSIONS.alerte_equipe` du serveur.
//
// ATTENTION : on N'UTILISE PAS le `can()` de `config/permissions.config.js`
// pour cette fonctionnalité. Ce helper court-circuite tout pour
// `ADMIN_PHARMAKON` (« l'admin passe partout »), or le §5.6 dit EXACTEMENT
// l'inverse ici : les alertes équipe sont des messages employeur → employé
// internes à UNE officine, et le support plateforme ne voit que des compteurs,
// jamais le contenu. Le serveur applique déjà cette exclusion ; ces gardes-ci
// évitent en plus d'AFFICHER un panneau qui ne rendrait que des 403.
//
// Ce ne sont que des gardes d'affichage — l'autorité reste le serveur.
// ─────────────────────────────────────────────────────────────────────────────

const ROLES_EQUIPE = Object.freeze(["PROPRIETAIRE", "MANAGER", "CAISSIER"]);
const ROLES_REDACTION = Object.freeze(["PROPRIETAIRE", "MANAGER"]);
/** Q2 — le palier bloquant arrête le comptoir : le titulaire seul l'émet. */
const ROLES_URGENT = Object.freeze(["PROPRIETAIRE"]);

/** Ce compte reçoit-il un flux d'alertes ? (jamais ADMIN_PHARMAKON — §5.6) */
export function peutRecevoirAlertes(user) {
  return !!user?.pharmacie_id && ROLES_EQUIPE.includes(user?.role);
}

/** Ce compte peut-il rédiger et envoyer ? (Q3 — le caissier est CIBLE, jamais auteur) */
export function peutRediger(user) {
  return !!user?.pharmacie_id && ROLES_REDACTION.includes(user?.role);
}

/** Ce compte peut-il émettre un URGENT ? (Q2) */
export function peutEnvoyerUrgent(user) {
  return !!user?.pharmacie_id && ROLES_URGENT.includes(user?.role);
}

/** Ce compte peut-il lire le tableau d'accusés ? (Q6 — jamais le support plateforme) */
export function peutVoirAccuses(user) {
  return peutRediger(user);
}
